"use client"

import { useEffect, useMemo, useState } from "react"
import { useTranslations } from "next-intl"
import { Checkbox } from "@/components/ui/checkbox"
import { Button } from "@/components/ui/button"
import { DialogCard } from "@/components/ui/dialog-card"
import { DockSheet } from "@/components/ui/dock-sheet"
import { useMediaQuery } from "@/hooks/use-media-query"
import type { ListingRow } from "@/types/listing"
import { creationFlowPrimaryCtaClass } from "@/lib/creation-flow-form-styles"

export function AddItemsToCollectionDialog({
  open,
  onOpenChange,
  collectionTitle,
  candidates,
  isAdding = false,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  collectionTitle: string
  candidates: ListingRow[]
  isAdding?: boolean
  onConfirm: (listingIds: string[]) => void
}) {
  const tML = useTranslations("myListings")
  const tColl = useTranslations("collections")
  const tCommon = useTranslations("common")
  const isDesktop = useMediaQuery("(min-width: 768px)")
  const [picked, setPicked] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (!open) setPicked(new Set())
  }, [open])

  const sortedCandidates = useMemo(
    () => [...candidates].sort((a, b) => a.title.localeCompare(b.title)),
    [candidates],
  )

  const toggle = (id: string) => {
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const saveDisabled = picked.size === 0 || isAdding
  const saveLabel = isAdding ? tColl("savingShort") : tCommon("save")

  const listBody = (
    <div className="space-y-2">
      {sortedCandidates.length === 0 ? (
        <p className="text-base text-muted-foreground py-6 text-center">{tML("addItemsNoneAvailable")}</p>
      ) : (
        sortedCandidates.map((listing) => {
          const checked = picked.has(listing.id)
          return (
            <div
              key={listing.id}
              role="button"
              tabIndex={0}
              onClick={() => toggle(listing.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  toggle(listing.id)
                }
              }}
              className="flex w-full cursor-pointer items-center gap-3 rounded-xl border border-foreground/10 p-3 text-left active:bg-foreground/5"
            >
              <Checkbox
                checked={checked}
                onCheckedChange={() => toggle(listing.id)}
                onClick={(e) => e.stopPropagation()}
              />
              <span className="flex-1 min-w-0 text-base font-medium text-foreground line-clamp-2">
                {listing.title}
              </span>
            </div>
          )
        })
      )}
    </div>
  )

  const saveButton = (
    <Button
      type="button"
      disabled={saveDisabled}
      className={creationFlowPrimaryCtaClass}
      onClick={() => onConfirm(Array.from(picked))}
    >
      {saveLabel}
    </Button>
  )

  if (!isDesktop) {
    return (
      <DockSheet
        open={open}
        onOpenChange={onOpenChange}
        title={tML("addItemsToCollection")}
        footer={saveButton}
      >
        {collectionTitle ? (
          <p className="text-base text-muted-foreground pb-3">{collectionTitle}</p>
        ) : null}
        {listBody}
      </DockSheet>
    )
  }

  return (
    <DialogCard
      open={open}
      onOpenChange={onOpenChange}
      title={tML("addItemsToCollection")}
      belowTitle={
        collectionTitle ? (
          <p className="truncate text-center text-sm text-muted-foreground">{collectionTitle}</p>
        ) : null
      }
      panelClassName="max-w-lg h-[80dvh]"
      footer={saveButton}
    >
      {listBody}
    </DialogCard>
  )
}
